import type { GibanjeKazalnika, VrsticaGibanja } from '@koda/shared';
import type { JSX } from 'react';

import { znesek } from '../lib/format';
import { useBesedila } from '../lib/jezik';
import { celicaDelov, sPredznakom } from './kazalniki-skupno';
import type { Celica } from './PanelRazclenitve';

/**
 * Gibanje stanja v »Poglej izračun« okenca Gibanje – na telefonu, kjer okenca
 * ni, v izračunu kazalnika (terjatve, zaloge, obveznosti do dobaviteljev;
 * 6.3): kratka razlaga spremembe z besedami, nato uporabljene
 * številke – promet obeh let, povprečno stanje, stanje na zadnji dan, od
 * začetka leta in lani – in na koncu, kako se računa. Zneska, ki sta vsota
 * knjižb (promet, stanje na dan), odpreta knjižbe; povprečje stanj je izračun.
 */
export function GibanjeIzracun({
  gibanje: g,
  naCelico,
  brezNaslova = false,
}: {
  gibanje: GibanjeKazalnika;
  naCelico: (celica: Celica) => void;
  /** Naslov ima že okvir izračuna (izračun, odprt iz okenca »Gibanje«). */
  brezNaslova?: boolean;
}): JSX.Element {
  const b = useBesedila();
  const odst = (v: string): string => `${sPredznakom(v)} %`;
  const ot = (v: string): string => `${sPredznakom(v)} ${b.pk.odstotneTocke}`;
  const p = g.povprecje;
  const st = g.stanje;

  const razlaga: string[] = [];
  if (p.rast !== null && g.rastPrometa !== null && p.obdobjeZdaj !== null && p.obdobjePrej !== null) {
    razlaga.push(b.pk.gibanjeUvod(p.obdobjeZdaj, p.obdobjePrej, odst(p.rast), g.nazivPrometa, odst(g.rastPrometa)));
  }
  if (p.razlika !== null) {
    const r = Number(p.razlika);
    razlaga.push(b.pk.gibanjeRazmerje(ot(p.razlika), r > 0 ? 'vecje' : r < 0 ? 'manjse' : 'enako'));
  }
  if (st.rast !== null && st.obdobjeZdaj !== null && st.obdobjePrej !== null) {
    razlaga.push(b.pk.gibanjeNaDan(st.obdobjeZdaj, st.obdobjePrej, odst(st.rast), st.razlika === null ? null : ot(st.razlika)));
  }
  if (g.odZacetka.rast !== null || g.lani.rast !== null) {
    razlaga.push(
      b.pk.gibanjeLeto(g.odZacetka.rast === null ? null : odst(g.odZacetka.rast), g.lani.rast === null ? null : odst(g.lani.rast)),
    );
  }

  const znesekCelice = (v: VrsticaGibanja, kdaj: 'prej' | 'zdaj'): JSX.Element => {
    const z = kdaj === 'prej' ? v.prej : v.zdaj;
    const obdobje = (kdaj === 'prej' ? v.obdobjePrej : v.obdobjeZdaj) ?? '';
    const deli = kdaj === 'prej' ? v.deliPrej : v.deliZdaj;
    if (z === null) return <td className="stevilka">{b.pk.ni}</td>;
    return (
      <td className="stevilka">
        <span className="gib-obdobje">{obdobje}</span>
        {deli === undefined || deli.length === 0 ? (
          <span>{znesek(z)}</span>
        ) : (
          <button
            type="button"
            className="znesek"
            title={b.namigCelice}
            onClick={() => {
              naCelico(celicaDelov(v.naziv, obdobje, z, deli));
            }}
          >
            {znesek(z)}
          </button>
        )}
      </td>
    );
  };
  const vrstica = (v: VrsticaGibanja, razlika?: string | null): JSX.Element => (
    <tr key={v.naziv}>
      <td className="postavka">{`${v.naziv.charAt(0).toLocaleUpperCase()}${v.naziv.slice(1)}`}</td>
      {znesekCelice(v, 'prej')}
      {znesekCelice(v, 'zdaj')}
      <td className="stevilka" title={v.rast === null ? (v.razlog ?? undefined) : undefined}>
        {v.rast === null ? b.pk.ni : odst(v.rast)}
      </td>
      <td className="stevilka">{razlika === undefined || razlika === null ? '' : ot(razlika)}</td>
    </tr>
  );

  return (
    <section className="gibanje-izracun" aria-label={b.pk.gibanjeStanja}>
      {!brezNaslova && <h4>{b.pk.gibanjeStanja}</h4>}
      {razlaga.length > 0 && (
        <div className="razlaga-kazalnika">
          {razlaga.map((t) => (
            <p key={t}>{t}</p>
          ))}
        </div>
      )}
      <table className="sestavine-izracuna gibanje-stevilke">
        <thead>
          <tr>
            <th>{b.pk.gibanjeStolpci.postavka}</th>
            <th className="stevilka">{b.pk.gibanjeStolpci.prej}</th>
            <th className="stevilka">{b.pk.gibanjeStolpci.zdaj}</th>
            <th className="stevilka">{b.pk.gibanjeStolpci.sprememba}</th>
            <th className="stevilka">{b.pk.gibanjeStolpci.protiPrometu}</th>
          </tr>
        </thead>
        <tbody>
          {g.promet !== undefined && vrstica(g.promet)}
          {vrstica(p, p.razlika)}
          {vrstica(st, st.razlika)}
          {vrstica(g.odZacetka)}
          {vrstica(g.lani)}
        </tbody>
      </table>
      <details className="po-mesecih">
        <summary>{b.pk.kakoSeRacuna}</summary>
        <div className="razlaga-kazalnika">
          {b.pk.gibanjePomen(g).map((t) => (
            <p key={t}>{t}</p>
          ))}
        </div>
      </details>
    </section>
  );
}
